import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { describeActionError } from '@/lib/errors/lifecycleRefusal';
import {
  MapPin,
  Trash2,
  AlertTriangle,
  Building,
  User,
  CheckSquare,
  Edit3,
  Calendar
} from 'lucide-react';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useRecallAssignmentStatus } from '@/hooks/useRecallAssignmentStatus';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { checkExistingAssignment } from '@/lib/supabase/technicianAssignments';
import BatchAssignmentPanel from './BatchAssignmentPanel';
import { ErrorBanner } from '@/components/ErrorBanner';
import { InlineError } from '@/components/InlineError';
import { useDataLoadingError, useUserActionError } from '@/hooks/useErrorDisplay';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import { logUserActivity } from '@/lib/activity/userActivity';

const AssignTechnicianStep: React.FC = () => {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const tenantTimezone = currentTenant?.time_zone || 'America/New_York'; // Default to Eastern if not set

  // Get assigner name from user metadata or email
  const assignerName =
    user?.user_metadata?.['full_name'] || user?.email?.split('@')[0] || 'Unknown';
  const {
    selectedRecalls,
    groupedRecalls,
    loading,
    error,
    errorMetadata: selectedRecallsErrorMetadata,
    stats,
    isAuthenticated,
    removeRecall,
    clearAllRecalls,
    refreshData: refreshSelectedRecalls
  } = useSelectedRecalls();

  const {
    assignments,
    activeTechnicians,
    loading: techniciansLoading,
    error: technicianError,
    errorMetadata: assignmentsErrorMetadata,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    isCreating,
    isUpdating,
    refreshData: refreshAssignments
  } = useTechnicianAssignments();

  // Track completed manual stops to filter them from all views
  const { completedManualStops } = useRecallAssignmentStatus();

  // Get error display configs
  const dataLoadingError = useDataLoadingError(
    selectedRecallsErrorMetadata || assignmentsErrorMetadata,
    'loading routing data',
    () => {
      refreshSelectedRecalls();
      refreshAssignments();
    }
  );

  const [groupBy, setGroupBy] = useState<'dealer' | 'skillLevel'>('dealer');
  const [assignmentForms, setAssignmentForms] = useState<
    Record<string, { technician_id: string; notes: string; expected_visit_date?: Date }>
  >({});
  const [selectedRecallsForBatch, setSelectedRecallsForBatch] = useState<Set<string>>(new Set());
  const [assignmentMode, setAssignmentMode] = useState<'individual' | 'batch'>('individual');

  // Dialog state for editing assignments
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAssignmentData, setEditingAssignmentData] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    technician_id: '',
    notes: '',
    expected_visit_date: undefined as Date | undefined
  });

  // Success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Helper function to identify assigned technicians (those with assignments)
  const assignedTechnicianIds = useMemo(() => {
    return new Set(assignments.map(assignment => assignment.technician_id));
  }, [assignments]);

  // Filter out recalls that have been processed (confirmed, completed, or cancelled)
  // Once a recall's status is updated in route tracker, it should not appear in the assignment page
  const unconfirmedRecalls = useMemo(() => {
    return selectedRecalls.filter(recall => {
      // Exclude completed manual stops from all views
      // They should only be visible in the route tracker completed section
      const recallKey = `${recall.vin}|${recall.campaign_number}`;
      if (completedManualStops.has(recallKey)) {
        return false;
      }

      const existingAssignment = assignments.find(
        a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
      );

      if (!existingAssignment) {
        // No assignment exists, show the recall
        return true;
      }

      const status = (existingAssignment as any).assignment_status;
      // Only show recalls that are still in active workflow states
      // Exclude: confirmed, completed, cancelled (these have been processed)
      // Include: assigned, in_progress (these can still be reassigned)
      return status !== 'confirmed' && status !== 'completed' && status !== 'cancelled';
    });
  }, [selectedRecalls, assignments, completedManualStops]);

  // Filter grouped recalls to only include unconfirmed recalls
  const unconfirmedGroupedRecalls = useMemo(() => {
    const filtered: Record<string, any> = {};

    Object.entries(groupedRecalls).forEach(([dealerKey, dealerGroup]) => {
      const unconfirmedDealerRecalls = (dealerGroup as any).recalls?.filter((recall: any) => {
        // Exclude completed manual stops from all views
        const recallKey = `${recall.vin}|${recall.campaign_number}`;
        if (completedManualStops.has(recallKey)) {
          return false;
        }

        const existingAssignment = assignments.find(
          a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
        );

        if (!existingAssignment) {
          return true;
        }

        const status = (existingAssignment as any).assignment_status;
        // Exclude: confirmed, completed, cancelled (these have been processed)
        // Include: assigned, in_progress (these can still be reassigned)
        return status !== 'confirmed' && status !== 'completed' && status !== 'cancelled';
      });

      if (unconfirmedDealerRecalls && unconfirmedDealerRecalls.length > 0) {
        filtered[dealerKey] = {
          ...(dealerGroup as any),
          recalls: unconfirmedDealerRecalls
        };
      }
    });

    return filtered;
  }, [groupedRecalls, assignments, completedManualStops]);

  // Start with individual mode by default
  // (users can switch to batch mode if needed)

  const getExistingAssignment = (vin: string, campaignNumber: string) => {
    return assignments.find(
      assignment => assignment.vin === vin && assignment.campaign_number === campaignNumber
    );
  };

  const handleAssignmentFormChange = (
    recallKey: string,
    field: string,
    value: string | Date | undefined
  ) => {
    setAssignmentForms(prev => {
      const existing = prev[recallKey] || {
        technician_id: '',
        notes: '',
        expected_visit_date: new Date()
      }; // Default to today
      return {
        ...prev,
        [recallKey]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  // Helper function to convert Date to ISO datetime string (for TIMESTAMPTZ storage)
  const dateToISOString = (date: Date): string => {
    return date.toISOString();
  };

  // Helper function to convert Date to local date string (YYYY-MM-DD) without timezone shift
  // Kept for backward compatibility if needed
  const dateToLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format datetime string for display using tenant timezone
  // Handles both ISO datetime strings and date-only strings (YYYY-MM-DD) for backward compatibility
  // IMPORTANT: Database returns TIMESTAMPTZ like "2026-01-27T00:00:00+00:00" - we extract the date
  // and parse as local date to avoid UTC timezone shift
  const formatDateStringForDisplay = (dateString: string): string => {
    try {
      // Extract YYYY-MM-DD from the string (handles both date-only and timestamps)
      const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

      let date: Date;
      if (dateMatch && dateMatch[1] && dateMatch[2] && dateMatch[3]) {
        // Parse as local date to avoid UTC timezone shift
        // "2026-01-25" or "2026-01-25T00:00:00+00:00" should display as Jan 25
        const year = parseInt(dateMatch[1], 10);
        const month = parseInt(dateMatch[2], 10) - 1;
        const day = parseInt(dateMatch[3], 10);
        date = new Date(year, month, day, 12, 0, 0); // Noon local time to avoid edge cases
      } else {
        date = new Date(dateString);
      }

      // Check if original string was date-only (for display format decision)
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);

      if (isNaN(date.getTime())) {
        return dateString; // Invalid date, return as-is
      }

      if (isDateOnly) {
        // For date-only strings, just show the date without time
        return new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }).format(date);
      } else {
        // Format date and time (preferred format)
        return new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: tenantTimezone
        }).format(date);
      }
    } catch {
      // If parsing fails, return the raw string
      return dateString;
    }
  };

  const handleAutoAssign = async (recall: any, technicianId: string) => {
    const recallKey = `${recall.vin}-${recall.campaign_number}`;
    const formData = assignmentForms[recallKey];
    const visitDate = formData?.expected_visit_date;

    if (!visitDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select a visit date first',
        variant: 'default'
      });
      return;
    }

    // Check if assignment already exists by querying the database directly
    // This ensures we have the most up-to-date information
    let existingAssignment;
    try {
      existingAssignment = await checkExistingAssignment(recall.vin, recall.campaign_number);
    } catch {
      // Continue anyway - we'll try to create if not found
      existingAssignment = null;
    }

    try {
      // Convert to Date object if it's not already
      const dateObj = visitDate instanceof Date ? visitDate : new Date(visitDate);

      // Validate the date
      if (isNaN(dateObj.getTime())) {
        throw new Error('Invalid date selected');
      }

      // Use local date string to preserve the user's selected date
      // (toISOString would shift to UTC which can change the date)
      const visitDateString = dateToLocalDateString(dateObj);

      if (existingAssignment) {
        // Validate that we have a valid assignment ID
        if (!existingAssignment.id) {
          throw new Error('Invalid assignment: missing assignment ID');
        }

        // Update existing assignment (reassignment or date change)
        // Only include fields that should be updated
        const updateData: {
          technician_id: string;
          notes?: string;
          expected_visit_date: string;
        } = {
          technician_id: technicianId,
          expected_visit_date: visitDateString
        };

        // Only include notes if they were provided
        if (formData?.notes !== undefined && formData.notes !== null) {
          updateData.notes = formData.notes;
        }

        try {
          await updateAssignment(existingAssignment.id, updateData);
        } catch (updateError: any) {
          throw updateError; // Re-throw to be caught by outer catch
        }
      } else {
        // Create new assignment
        await createAssignment({
          technician_id: technicianId,
          vin: recall.vin,
          campaign_number: recall.campaign_number,
          recall_title: recall.recall_title,
          dealer_name: recall.dealer_name,
          priority: 'medium',
          estimated_duration_minutes: 60,
          notes: formData?.notes || '',
          expected_visit_date: visitDateString,
          assigned_by: user?.id,
          assigned_by_name: assignerName
        });
      }

      // Show success message and toast
      const techName =
        activeTechnicians.find(t => t.id === technicianId)?.full_name || 'technician';
      const successMessage = isUpdate
        ? `Updated assignment to ${techName}`
        : `Assigned to ${techName}`;

      setSuccessMessage(`✓ ${successMessage}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      toast({
        title: isUpdate ? 'Assignment Updated' : 'Assignment Created',
        description: successMessage,
        variant: 'default'
      });

      // Clear the form
      setAssignmentForms(prev => {
        const newForms = { ...prev };
        delete newForms[recallKey];
        return newForms;
      });
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.error?.message ||
        error?.toString() ||
        `Failed to ${existingAssignment ? 'update' : 'assign'} recall. Please try again.`;

      toast({
        title: 'Assignment Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleBatchAssign = async (
    recallIds: string[],
    technicianId: string,
    notes: string,
    expectedVisitDate?: Date
  ) => {
    if (!expectedVisitDate) {
      toast({
        title: 'Date Required',
        description: 'Please select an expected visit date for the batch assignment.',
        variant: 'destructive'
      });
      return;
    }

    const visitDateString = dateToLocalDateString(expectedVisitDate);

    try {
      // Filter recalls where the VIN is in the selected list (recallIds contains VINs)
      const recallsToAssign = selectedRecalls.filter(recall => recallIds.includes(recall.vin));

      if (recallsToAssign.length === 0) {
        toast({
          title: 'No Selection',
          description: 'No recalls selected for assignment.',
          variant: 'destructive'
        });
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      let newAssignmentsCount = 0;
      let updatedAssignmentsCount = 0;
      const technicianName =
        activeTechnicians.find(t => t.id === technicianId)?.full_name || 'technician';

      for (const recall of recallsToAssign) {
        try {
          // Check if assignment already exists
          const existingAssignment = assignments.find(
            a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
          );

          if (existingAssignment) {
            // Update existing assignment (reassignment or schedule change)
            await updateAssignment(existingAssignment.id, {
              technician_id: technicianId,
              notes: notes,
              expected_visit_date: visitDateString
            });
            updatedAssignmentsCount++;
          } else {
            // Create new assignment
            await createAssignment({
              technician_id: technicianId,
              vin: recall.vin,
              campaign_number: recall.campaign_number,
              recall_title: recall.recall_title,
              dealer_name: recall.dealer_name,
              priority: 'medium',
              estimated_duration_minutes: 60,
              notes: notes,
              expected_visit_date: visitDateString,
              assigned_by: user?.id,
              assigned_by_name: assignerName
            });
            newAssignmentsCount++;
          }
          successCount++;
        } catch (err) {
          console.error('Failed to assign recall:', recall.campaign_number, err);
          errorCount++;
        }
      }

      // Clear selected recalls after successful assignments
      setSelectedRecallsForBatch(new Set());

      if (errorCount > 0) {
        toast({
          title: 'Partial Success',
          description: `Processed ${successCount} recall${successCount !== 1 ? 's' : ''} successfully. ${errorCount} failed.`,
          variant: 'destructive'
        });
      } else {
        let description = '';
        if (newAssignmentsCount > 0 && updatedAssignmentsCount > 0) {
          description = `Created ${newAssignmentsCount} new assignment${newAssignmentsCount !== 1 ? 's' : ''} and updated ${updatedAssignmentsCount} existing assignment${updatedAssignmentsCount !== 1 ? 's' : ''} for ${technicianName}.`;
        } else if (newAssignmentsCount > 0) {
          description = `Successfully created ${newAssignmentsCount} new assignment${newAssignmentsCount !== 1 ? 's' : ''} for ${technicianName}.`;
        } else if (updatedAssignmentsCount > 0) {
          description = `Successfully updated ${updatedAssignmentsCount} assignment${updatedAssignmentsCount !== 1 ? 's' : ''} for ${technicianName}.`;
        } else {
          description = `Successfully processed ${successCount} recall${successCount !== 1 ? 's' : ''} for ${technicianName}.`;
        }

        toast({
          title: 'Batch Assignment Complete',
          description: description,
          variant: 'default'
        });
      }

      // Log batch assignment activity (only if any succeeded)
      if (successCount > 0) {
        logUserActivity({
          category: 'routing',
          action: 'batch_recalls_assigned',
          entityType: 'technician',
          entityId: technicianId,
          entityName: technicianName,
          itemCount: successCount,
          metadata: {
            technician_id: technicianId,
            technician_name: technicianName,
            new_assignments: newAssignmentsCount,
            updated_assignments: updatedAssignmentsCount,
            failed_count: errorCount,
            expected_visit_date: visitDateString
          }
        });
      }
    } catch (error) {
      console.error('Error in batch assignment:', error);
      toast({
        title: 'Assignment Failed',
        description: (error as Error).message || 'Failed to create batch assignments.',
        variant: 'destructive'
      });
    }
  };

  const handleToggleBatchSelect = (recallId: string) => {
    setSelectedRecallsForBatch(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recallId)) {
        newSet.delete(recallId);
      } else {
        newSet.add(recallId);
      }
      return newSet;
    });
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (confirm('Are you sure you want to remove this assignment?')) {
      try {
        await deleteAssignment(assignmentId);
        toast({
          title: 'Assignment Removed',
          description: 'The assignment has been successfully removed.',
          variant: 'default'
        });
      } catch (error) {
        console.error('Error removing assignment:', error);
        toast({
          title: 'Removal Failed',
          description: 'Failed to remove the assignment. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleEditAssignment = (assignmentId: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (assignment) {
      setEditingAssignmentData(assignment);
      // Parse expected_visit_date as local date to avoid UTC timezone shift
      // Database returns TIMESTAMPTZ like "2026-01-27T00:00:00+00:00"
      let parsedDate: Date | undefined;
      if (assignment.expected_visit_date) {
        const dateStr = assignment.expected_visit_date;
        const dateMatch = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch && dateMatch[1] && dateMatch[2] && dateMatch[3]) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1;
          const day = parseInt(dateMatch[3], 10);
          parsedDate = new Date(year, month, day, 12, 0, 0);
        } else {
          parsedDate = new Date(dateStr);
        }
      }

      setEditFormData({
        technician_id: assignment.technician_id,
        notes: assignment.notes || '',
        expected_visit_date: parsedDate
      });
      setEditDialogOpen(true);
    }
  };

  const handleUpdateAssignment = async () => {
    if (!editFormData.technician_id) {
      toast({
        title: 'Validation Error',
        description: 'Please select a technician',
        variant: 'default'
      });
      return;
    }

    if (!editingAssignmentData) {
      toast({
        title: 'Error',
        description: 'No assignment data found',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Prepare date string if date is provided
      let visitDateString: string | undefined;
      if (editFormData.expected_visit_date) {
        try {
          const dateObj =
            editFormData.expected_visit_date instanceof Date
              ? editFormData.expected_visit_date
              : new Date(editFormData.expected_visit_date);

          if (!isNaN(dateObj.getTime())) {
            // Use local date string to preserve the user's selected date
            visitDateString = dateToLocalDateString(dateObj);
          }
        } catch (dateError) {
          console.error('Error converting date:', dateError);
          throw new Error('Invalid date format');
        }
      }

      await updateAssignment(editingAssignmentData.id, {
        technician_id: editFormData.technician_id,
        notes: editFormData.notes,
        ...(visitDateString && { expected_visit_date: visitDateString })
      });

      // Get technician name for success message
      const techName =
        activeTechnicians.find(t => t.id === editFormData.technician_id)?.full_name || 'technician';

      // Determine what was changed
      const wasTechnicianChanged =
        editingAssignmentData.technician_id !== editFormData.technician_id;
      const wasDateChanged = editingAssignmentData.expected_visit_date !== visitDateString;
      const wasNotesChanged = editingAssignmentData.notes !== editFormData.notes;

      let description = '';
      if (wasTechnicianChanged && wasDateChanged) {
        description = `Reassigned to ${techName} with new schedule date`;
      } else if (wasTechnicianChanged) {
        description = `Reassigned to ${techName}`;
      } else if (wasDateChanged) {
        description = `Schedule date updated for ${techName}`;
      } else if (wasNotesChanged) {
        description = `Assignment updated for ${techName}`;
      } else {
        description = `Assignment updated for ${techName}`;
      }

      toast({
        title: 'Assignment Updated',
        description: description,
        variant: 'default'
      });

      setEditDialogOpen(false);
      setEditingAssignmentData(null);
      setEditFormData({
        technician_id: '',
        notes: '',
        expected_visit_date: undefined
      });
    } catch (error: any) {
      console.error('Error updating assignment:', error);
      toast({
        title: 'Update Failed',
        description: error?.message || 'Failed to update the assignment. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingAssignmentData(null);
    setEditFormData({
      technician_id: '',
      notes: '',
      expected_visit_date: undefined
    });
  };

  const handleRemoveRecall = async (recall: any) => {
    if (
      confirm(
        `Are you sure you want to remove this recall from your route?\n\n${recall.recall_title}\nVIN: ${recall.vin}\nCampaign: ${recall.campaign_number}`
      )
    ) {
      try {
        await removeRecall(recall.vin, recall.campaign_number);
      } catch (error: any) {
        console.error('Error removing recall:', error);
        // Always surface something. The previous version only rendered a toast when the
        // message happened to contain 'confirmed', and swallowed every other failure —
        // the hook it relied on to "already show" one does not toast at all.
        const { title, description } = describeActionError(error, 'removing this recall');
        toast({ title, description, variant: 'destructive' });
      }
    }
  };

  const handleClearAllRecalls = async () => {
    if (
      confirm(
        `Are you sure you want to remove ALL unconfirmed recalls from your route?\n\nThis will remove ${unconfirmedRecalls.length} unconfirmed recalls and cannot be undone.`
      )
    ) {
      try {
        await clearAllRecalls();
      } catch (error) {
        console.error('Error clearing all recalls:', error);
        // Error toast is already shown by the hook, no need to show again
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className='p-6 text-center'>
          <AlertTriangle className='w-12 h-12 text-amber-500 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-foreground mb-2'>Authentication Required</h3>
          <p className='text-muted-foreground'>Please log in to access manual routing features.</p>
        </CardContent>
      </Card>
    );
  }

  // Render error banner in Layout slot if data loading error exists
  const errorBannerSlot =
    typeof document !== 'undefined' ? document.getElementById('error-banner-slot') : null;
  const shouldShowBanner = dataLoadingError && dataLoadingError.metadata;

  if (loading) {
    return (
      <Card>
        <CardContent className='p-6 text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto' />
          <p className='mt-4 text-muted-foreground'>Loading selected recalls...</p>
        </CardContent>
      </Card>
    );
  }

  if (unconfirmedRecalls.length === 0) {
    // Check if all recalls are confirmed
    const allRecallsConfirmed =
      selectedRecalls.length > 0 &&
      selectedRecalls.every(recall => {
        const assignment = assignments.find(
          a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
        );
        return assignment && (assignment as any).assignment_status === 'confirmed';
      });

    return (
      <Card>
        <CardContent className='p-6 text-center'>
          {allRecallsConfirmed ? (
            <>
              <CheckSquare className='w-12 h-12 text-green-500 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-foreground mb-2'>All Recalls Confirmed</h3>
              <p className='text-muted-foreground mb-4'>
                All selected recalls have been confirmed.
              </p>
              <Button onClick={() => (window.location.href = '/routing/tracker')}>
                View Confirmed Routes
              </Button>
            </>
          ) : (
            <>
              <MapPin className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
              <h3 className='text-lg font-medium text-foreground mb-2'>No Recalls Selected</h3>
              <p className='text-muted-foreground mb-4'>
                Go to the Recall List or Recall Map to select recalls for manual routing.
              </p>
              <Button onClick={() => (window.location.href = '/recalls')}>Go to Recall List</Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Group recalls by dealer or skill level, then by VIN within each group
  // Only show unconfirmed recalls (confirmed recalls are already locked in)
  const groupedData = Object.entries(
    groupBy === 'dealer'
      ? Object.keys(unconfirmedGroupedRecalls).reduce(
          (acc, dealerKey) => {
            const dealerGroup = unconfirmedGroupedRecalls[dealerKey];
            acc[dealerKey] = dealerGroup?.recalls || [];
            return acc;
          },
          {} as Record<string, any[]>
        )
      : unconfirmedRecalls.reduce(
          (acc, recall) => {
            const key = recall.skill_level || 'unset';
            if (!acc[key]) acc[key] = [];
            acc[key].push(recall);
            return acc;
          },
          {} as Record<string, any[]>
        )
  ).map(([groupKey, recalls]) => {
    // Group recalls by VIN within each main group
    const vinGroups = Array.isArray(recalls)
      ? recalls.reduce(
          (acc, recall) => {
            const vinKey = recall.vin;
            if (!acc[vinKey]) acc[vinKey] = [];
            acc[vinKey].push(recall);
            return acc;
          },
          {} as Record<string, any[]>
        )
      : {};

    return [groupKey, Object.entries(vinGroups)] as [string, [string, any[]][]];
  });

  return (
    <>
      {/* Error Banner - Portal to Layout slot */}
      {shouldShowBanner &&
        errorBannerSlot &&
        createPortal(
          <ErrorBanner
            title={dataLoadingError.metadata.userMessage}
            message={dataLoadingError.metadata.userMessage}
            onRetry={dataLoadingError.metadata.recoveryActions?.retry}
            onDismiss={() => {
              // Clear error state
              refreshSelectedRecalls();
              refreshAssignments();
            }}
          />,
          errorBannerSlot
        )}

      <div className='w-full h-full space-y-4 lg:space-y-6'>
        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>Unconfirmed Recalls</p>
                  <p className='text-2xl font-bold text-foreground'>{unconfirmedRecalls.length}</p>
                  {stats.totalRecalls > unconfirmedRecalls.length && (
                    <p className='text-xs text-muted-foreground mt-1'>
                      {stats.totalRecalls - unconfirmedRecalls.length} confirmed
                    </p>
                  )}
                </div>
                <MapPin className='w-8 h-8 text-blue-500 dark:text-blue-400' />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>Assignments</p>
                  <p className='text-2xl font-bold text-foreground'>
                    {
                      assignments.filter(assignment =>
                        unconfirmedRecalls.some(
                          recall =>
                            recall.vin === assignment.vin &&
                            recall.campaign_number === assignment.campaign_number
                        )
                      ).length
                    }
                  </p>
                </div>
                <User className='w-8 h-8 text-green-500 dark:text-green-400' />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>Unique Dealers</p>
                  <p className='text-2xl font-bold text-foreground'>{stats.uniqueDealers}</p>
                </div>
                <Building className='w-8 h-8 text-purple-500' />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4'>
              <div className='flex flex-col items-center justify-center h-full'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleClearAllRecalls}
                  className='text-red-600 hover:text-red-700 hover:bg-red-50 w-full'
                  disabled={selectedRecalls.length === 0}
                >
                  <Trash2 className='w-4 h-4 mr-2' />
                  Clear All Recalls
                </Button>
                <p className='text-xs text-muted-foreground mt-1 text-center'>
                  Remove all recalls from route
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assignment Mode Tabs */}
        <Tabs
          value={assignmentMode}
          onValueChange={value => setAssignmentMode(value as 'individual' | 'batch')}
        >
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='individual' className='flex items-center gap-2'>
              <User className='w-4 h-4' />
              Individual Assignment
            </TabsTrigger>
            <TabsTrigger value='batch' className='flex items-center gap-2'>
              <CheckSquare className='w-4 h-4' />
              Batch Assignment
            </TabsTrigger>
          </TabsList>

          <TabsContent value='batch' className='space-y-4'>
            <BatchAssignmentPanel
              recalls={selectedRecalls}
              technicians={activeTechnicians.map(tech => ({
                ...tech,
                phone_number: tech.phone_number || '',
                email: tech.email || '',
                current_assignments: 0,
                estimated_drive_time: 0,
                specialization: ''
              }))}
              assignments={assignments}
              onBatchAssign={handleBatchAssign}
              onToggleSelect={handleToggleBatchSelect}
              selectedRecalls={selectedRecallsForBatch}
              isCreating={isCreating}
            />
          </TabsContent>

          <TabsContent value='individual' className='space-y-4'>
            {/* Grouping Toggle */}
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle>Individual Technician Assignment</CardTitle>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant={groupBy === 'dealer' ? 'default' : 'outline'}
                      size='sm'
                      onClick={() => setGroupBy('dealer')}
                    >
                      Group by Dealer
                    </Button>
                    <Button
                      variant={groupBy === 'skillLevel' ? 'default' : 'outline'}
                      size='sm'
                      onClick={() => setGroupBy('skillLevel')}
                    >
                      Group by Skill Level
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {groupedData.map(([groupKey, vinGroups]) => (
                  <Card key={groupKey} className='mb-6 border-l-4 border-l-green-500'>
                    <CardHeader className='pb-3'>
                      <div className='flex items-center justify-between'>
                        <div>
                          <CardTitle className='text-lg font-semibold text-foreground'>
                            {groupBy === 'dealer' ? groupKey : `Skill Level: ${groupKey}`}
                          </CardTitle>
                          {(() => {
                            // Extract dealer info safely
                            if (groupBy !== 'dealer' || vinGroups.length === 0) return null;
                            const firstVinGroup = vinGroups[0];
                            if (
                              !firstVinGroup ||
                              !firstVinGroup[1] ||
                              firstVinGroup[1].length === 0
                            )
                              return null;
                            const firstRecall = firstVinGroup[1][0];
                            if (!firstRecall) return null;

                            return (
                              <div className='text-sm text-muted-foreground mt-1 flex items-center gap-1'>
                                <MapPin className='w-4 h-4' />
                                {firstRecall.dealer_street ||
                                  firstRecall.dealer_address ||
                                  'Address not available'}
                                , {firstRecall.dealer_city || 'Unknown'},{' '}
                                {firstRecall.dealer_region || 'Unknown'}
                                {firstRecall.dealer_postal_code
                                  ? ` ${firstRecall.dealer_postal_code}`
                                  : ''}
                              </div>
                            );
                          })()}
                        </div>
                        <Badge
                          variant='secondary'
                          className='text-sm bg-green-500/20 text-green-400 dark:bg-green-500/30 dark:text-green-300'
                        >
                          {vinGroups.reduce((total, [_, recalls]) => total + recalls.length, 0)}{' '}
                          total total recall
                          {vinGroups.reduce((total, [_, recalls]) => total + recalls.length, 0) !==
                          1
                            ? 's'
                            : ''}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className='pt-0'>
                      <div className='space-y-4'>
                        {/* VIN Groups within each main group */}
                        {vinGroups.map(([vinKey, recalls]) => (
                          <div key={vinKey} className='border rounded-lg p-4 bg-muted/50'>
                            <div className='flex items-center justify-between mb-3'>
                              <div>
                                <h4 className='text-base font-medium text-foreground'>
                                  {recalls.length > 0
                                    ? `${recalls[0].car_year} ${recalls[0].make_name} ${recalls[0].model_name}`
                                    : 'Vehicle Details'}
                                </h4>
                                {recalls.length > 0 && (
                                  <div className='text-sm text-muted-foreground mt-1'>
                                    VIN: {recalls[0].vin}
                                  </div>
                                )}
                              </div>
                              <Badge
                                variant='secondary'
                                className='text-xs bg-muted text-muted-foreground'
                              >
                                {recalls.length} recall{recalls.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>

                            <div className='space-y-3'>
                              {recalls.map(recall => {
                                const existingAssignment = getExistingAssignment(
                                  recall.vin,
                                  recall.campaign_number
                                );
                                const recallKey = `${recall.vin}-${recall.campaign_number}`;
                                const formData = assignmentForms[recallKey] || {
                                  technician_id: '',
                                  notes: '',
                                  expected_visit_date: new Date()
                                };

                                return (
                                  <div
                                    key={recallKey}
                                    className={cn(
                                      'border rounded-lg p-4 bg-card',
                                      existingAssignment
                                        ? 'border-blue-500 dark:border-blue-400 border-2'
                                        : 'border-blue-500/30 dark:border-blue-400/30'
                                    )}
                                  >
                                    <div className='flex items-start justify-between mb-3'>
                                      <div className='flex-1'>
                                        <div className='flex items-center gap-2 mb-3'>
                                          <Badge
                                            variant='outline'
                                            className='text-xs bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 dark:border-blue-400/30 shrink-0'
                                          >
                                            {recall.campaign_number}
                                          </Badge>
                                          <span className='font-medium text-foreground text-sm'>
                                            {recall.recall_title}
                                          </span>
                                          <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => handleRemoveRecall(recall)}
                                            className='h-7 px-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 dark:hover:bg-red-500/20'
                                            title='Remove this recall from route'
                                          >
                                            <Trash2 className='w-3.5 h-3.5' />
                                          </Button>
                                        </div>
                                      </div>

                                      <div className='flex items-start justify-between'>
                                        <div className='flex-1'>
                                          {existingAssignment ? (
                                            <div className='w-full'>
                                              <div className='space-y-2'>
                                                <div className='flex items-center gap-2 text-sm'>
                                                  <User className='w-4 h-4 text-green-600 dark:text-green-400' />
                                                  <span className='font-medium text-green-800 dark:text-green-200'>
                                                    Assigned to {existingAssignment.technician_name}
                                                  </span>
                                                </div>
                                                {existingAssignment.expected_visit_date && (
                                                  <div className='flex items-center gap-2 text-sm'>
                                                    <Calendar className='w-4 h-4 text-green-600 dark:text-green-400' />
                                                    <span className='text-green-700 dark:text-green-300'>
                                                      Scheduled on{' '}
                                                      {formatDateStringForDisplay(
                                                        existingAssignment.expected_visit_date
                                                      )}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                              {existingAssignment.notes && (
                                                <div className='flex items-start gap-2 text-sm mt-2'>
                                                  <div className='w-4 h-4 mt-0.5 flex-shrink-0'>
                                                    <svg
                                                      className='w-4 h-4 text-blue-600 dark:text-blue-400'
                                                      fill='currentColor'
                                                      viewBox='0 0 24 24'
                                                    >
                                                      <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z' />
                                                    </svg>
                                                  </div>
                                                  <span className='text-blue-600 dark:text-blue-400 italic'>
                                                    {existingAssignment.notes}
                                                  </span>
                                                </div>
                                              )}
                                              <div className='flex items-center gap-1 mt-2'>
                                                <Button
                                                  variant='outline'
                                                  size='sm'
                                                  onClick={() =>
                                                    handleEditAssignment(existingAssignment.id)
                                                  }
                                                  className='text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-500/10 dark:hover:bg-blue-500/20 border-blue-500/30 dark:border-blue-400/30'
                                                  title='Edit assignment'
                                                >
                                                  <Edit3 className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                  variant='outline'
                                                  size='sm'
                                                  onClick={() =>
                                                    handleRemoveAssignment(existingAssignment.id)
                                                  }
                                                  className='text-red-600 hover:text-red-700'
                                                >
                                                  <Trash2 className='w-4 h-4' />
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className='space-y-3'>
                                              {/* Success Message */}
                                              {successMessage && (
                                                <div className='p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2'>
                                                  <span>✓</span>
                                                  {successMessage}
                                                </div>
                                              )}

                                              {/* Assignment Form */}
                                              <div className='space-y-3 p-4 bg-blue-500/10 dark:bg-blue-500/20 rounded-lg border border-blue-500/30 dark:border-blue-400/30'>
                                                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                                  {/* Visit Date and Time */}
                                                  <div className='space-y-1 md:col-span-1'>
                                                    <label className='text-xs font-medium text-foreground'>
                                                      Scheduled Service Date & Time *
                                                    </label>
                                                    <DateTimePicker
                                                      value={formData.expected_visit_date}
                                                      onChange={date =>
                                                        handleAssignmentFormChange(
                                                          recallKey,
                                                          'expected_visit_date',
                                                          date
                                                        )
                                                      }
                                                      datePlaceholder='Select date'
                                                      timePlaceholder='HH:MM'
                                                      className='w-full'
                                                      timeStep={30}
                                                    />
                                                    <p className='text-xs text-muted-foreground'>
                                                      Date and time when technician is scheduled to
                                                      service this recall
                                                    </p>
                                                  </div>

                                                  {/* Technician Selection */}
                                                  <div className='space-y-1'>
                                                    <label className='text-xs font-medium text-foreground'>
                                                      Assign Technician *
                                                      {techniciansLoading && (
                                                        <span className='text-muted-foreground ml-1'>
                                                          (loading...)
                                                        </span>
                                                      )}
                                                    </label>
                                                    <Select
                                                      value={formData.technician_id}
                                                      onValueChange={value =>
                                                        handleAssignmentFormChange(
                                                          recallKey,
                                                          'technician_id',
                                                          value
                                                        )
                                                      }
                                                      disabled={
                                                        techniciansLoading ||
                                                        !formData.expected_visit_date
                                                      }
                                                    >
                                                      <SelectTrigger className='w-full h-9'>
                                                        <SelectValue
                                                          placeholder={
                                                            formData.expected_visit_date
                                                              ? 'Select technician...'
                                                              : 'Select date first'
                                                          }
                                                        />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        {activeTechnicians.map(technician => {
                                                          const isAssigned =
                                                            assignedTechnicianIds.has(
                                                              technician.id
                                                            );
                                                          const assignmentCount =
                                                            assignments.filter(
                                                              a => a.technician_id === technician.id
                                                            ).length;
                                                          const skillLabel =
                                                            technician.skill_level === 1
                                                              ? 'Low'
                                                              : technician.skill_level === 2
                                                                ? 'Med'
                                                                : technician.skill_level === 3
                                                                  ? 'High'
                                                                  : 'N/A';

                                                          return (
                                                            <SelectItem
                                                              key={technician.id}
                                                              value={technician.id}
                                                            >
                                                              <div className='flex items-center gap-2 py-1'>
                                                                <div
                                                                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                                    isAssigned
                                                                      ? 'bg-green-500/20 dark:bg-green-500/30'
                                                                      : 'bg-blue-500/20 dark:bg-blue-500/30'
                                                                  }`}
                                                                >
                                                                  <User
                                                                    className={`w-3 h-3 ${
                                                                      isAssigned
                                                                        ? 'text-green-600 dark:text-green-400'
                                                                        : 'text-blue-600 dark:text-blue-400'
                                                                    }`}
                                                                  />
                                                                </div>
                                                                <div className='flex-1'>
                                                                  <div className='font-medium text-sm text-foreground'>
                                                                    {technician.full_name}
                                                                    {isAssigned && (
                                                                      <span className='ml-1 text-xs text-green-600 dark:text-green-400'>
                                                                        ({assignmentCount})
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                  <Badge
                                                                    variant='outline'
                                                                    className={`text-xs ${
                                                                      technician.skill_level === 1
                                                                        ? 'border-red-500 text-red-700 dark:border-red-400 dark:text-red-300'
                                                                        : technician.skill_level ===
                                                                            2
                                                                          ? 'border-yellow-500 text-yellow-700 dark:border-yellow-400 dark:text-yellow-300'
                                                                          : technician.skill_level ===
                                                                              3
                                                                            ? 'border-green-500 text-green-700 dark:border-green-400 dark:text-green-300'
                                                                            : 'border-border text-muted-foreground'
                                                                    }`}
                                                                  >
                                                                    {skillLabel}
                                                                  </Badge>
                                                                </div>
                                                              </div>
                                                            </SelectItem>
                                                          );
                                                        })}
                                                      </SelectContent>
                                                    </Select>
                                                  </div>

                                                  {/* Notes */}
                                                  <div className='space-y-1'>
                                                    <label className='text-xs font-medium text-foreground'>
                                                      Notes (Optional)
                                                    </label>
                                                    <Input
                                                      placeholder='Add notes...'
                                                      value={formData.notes}
                                                      onChange={e =>
                                                        handleAssignmentFormChange(
                                                          recallKey,
                                                          'notes',
                                                          e.target.value
                                                        )
                                                      }
                                                      className='w-full h-9 text-sm'
                                                    />
                                                  </div>
                                                </div>

                                                {/* Assignment Button */}
                                                {formData.technician_id &&
                                                  formData.expected_visit_date && (
                                                    <div className='mt-3 pt-3 border-t border-blue-500/30'>
                                                      <Button
                                                        onClick={() => {
                                                          handleAutoAssign(
                                                            recall,
                                                            formData.technician_id
                                                          ).catch(() => {
                                                            // Error already handled in handleAutoAssign
                                                          });
                                                        }}
                                                        className='w-full h-9 bg-blue-600 hover:bg-blue-700 text-white'
                                                        disabled={isCreating}
                                                      >
                                                        {isCreating ? (
                                                          <>
                                                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                                                            Assigning...
                                                          </>
                                                        ) : (
                                                          <>
                                                            <User className='w-4 h-4 mr-2' />
                                                            Assign to{' '}
                                                            {
                                                              activeTechnicians.find(
                                                                t => t.id === formData.technician_id
                                                              )?.full_name
                                                            }
                                                          </>
                                                        )}
                                                      </Button>
                                                    </div>
                                                  )}

                                                {!formData.expected_visit_date && (
                                                  <div className='text-xs text-blue-700 dark:text-blue-300'>
                                                    ℹ️ Select a visit date to enable technician
                                                    selection
                                                  </div>
                                                )}

                                                {formData.expected_visit_date &&
                                                  !formData.technician_id && (
                                                    <div className='text-xs text-blue-700 dark:text-blue-300'>
                                                      ℹ️ Select a technician to create assignment
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Assignment Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className='sm:max-w-[500px] overflow-visible'>
            <DialogHeader>
              <DialogTitle>Edit Assignment</DialogTitle>
            </DialogHeader>
            <div className='grid gap-4 py-4 overflow-visible'>
              {editingAssignmentData && (
                <>
                  <div className='space-y-2'>
                    <Label>Recall Information</Label>
                    <div className='p-3 bg-gray-50 rounded-lg'>
                      <div className='font-medium text-sm'>
                        {editingAssignmentData.recall_title}
                      </div>
                      <div className='text-xs text-gray-600 mt-1'>
                        VIN: {editingAssignmentData.vin} • Campaign:{' '}
                        {editingAssignmentData.campaign_number}
                      </div>
                    </div>
                  </div>

                  <div className='grid gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='technician'>Technician</Label>
                      <Select
                        value={editFormData.technician_id}
                        onValueChange={value =>
                          setEditFormData({ ...editFormData, technician_id: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select technician...' />
                        </SelectTrigger>
                        <SelectContent>
                          {activeTechnicians.map(technician => {
                            const isAssigned = assignedTechnicianIds.has(technician.id);
                            const assignmentCount = assignments.filter(
                              a => a.technician_id === technician.id
                            ).length;
                            const skillLabel =
                              technician.skill_level === 1
                                ? 'Low'
                                : technician.skill_level === 2
                                  ? 'Med'
                                  : technician.skill_level === 3
                                    ? 'High'
                                    : 'N/A';
                            const activeStatus = technician.is_active ? 'Active' : 'Inactive';

                            return (
                              <SelectItem key={technician.id} value={technician.id}>
                                <div className='flex items-center gap-2 py-1'>
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                                      isAssigned ? 'bg-green-100' : 'bg-blue-100'
                                    }`}
                                  >
                                    <User
                                      className={`w-3 h-3 ${
                                        isAssigned ? 'text-green-600' : 'text-blue-600'
                                      }`}
                                    />
                                  </div>
                                  <span className='font-medium text-sm text-gray-900'>
                                    {technician.full_name}
                                  </span>
                                  {isAssigned && (
                                    <span className='text-xs text-green-600'>
                                      ({assignmentCount})
                                    </span>
                                  )}
                                  <Badge variant='outline' className='text-xs shrink-0'>
                                    {skillLabel}
                                  </Badge>
                                  <span
                                    className={`text-xs shrink-0 ${technician.is_active ? 'text-green-600' : 'text-gray-500'}`}
                                  >
                                    {activeStatus}
                                  </span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='visit_date'>Scheduled Service Date & Time</Label>
                      <DateTimePicker
                        value={editFormData.expected_visit_date}
                        onChange={date =>
                          setEditFormData(prev => ({ ...prev, expected_visit_date: date }))
                        }
                        datePlaceholder='Select date'
                        timePlaceholder='HH:MM'
                        className='w-full'
                        timeStep={30}
                      />
                      <p className='text-xs text-muted-foreground'>
                        Date and time when technician is scheduled to service this recall
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='notes' className='text-sm text-gray-600'>
                        Notes (Optional)
                      </Label>
                      <Input
                        id='notes'
                        placeholder='Add notes...'
                        value={editFormData.notes}
                        onChange={e => setEditFormData({ ...editFormData, notes: e.target.value })}
                        className='w-full text-sm'
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateAssignment}
                disabled={!editFormData.technician_id || isUpdating}
                className='bg-green-600 hover:bg-green-700'
              >
                {isUpdating ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default AssignTechnicianStep;
